import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import { MdArrowBack } from "react-icons/md";
import { MultiSelect } from "primereact/multiselect";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import SkillSelect from "./SkillSelect";
import { PrimaryButton, SecondaryButton } from "../HR/hrComponents";

// HRMS tenants only: someone is already an HR employee (created through HR's own "Add employee", which already gave
// them a login) and now needs booking details - service, fee, appointment slots, shifts - so they can be assigned
// bookings. No identity is collected here and no new login is ever created; that only happens once, in HR.
// This screen collects the service and experience date, then hands off to the existing shift/fee screen
// (ProviderAvailability.jsx), which posts to `attach_service_provider` instead of `service_providers_register`
// because `existing_user_id` is set.
export default function AttachServiceProvider() {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeLabel, setEmployeeLabel] = useState("");
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [experienceStartDate, setExperienceStartDate] = useState("");
  const [skillKey, setSkillKey] = useState("PROFESSIONAL");
  const [error, setError] = useState("");
  // Employees added with the Crew role who still have no services / shifts. Only they can be picked: providers are
  // Crew-role people, and the API refuses anyone else (or anyone already set up).
  const [crew, setCrew] = useState([]);
  const [crewLoaded, setCrewLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("get_ezp_service");
        if (res.status === 200) setServices((res.data || []).map((s) => ({ id: s.service_id, name: s.service_name })));
      } catch (err) {
        toast.error("Failed to load services");
      }
    })();
    (async () => {
      try {
        const res = await apicall.get("crew_needing_details");
        if (res.success && Array.isArray(res.data)) setCrew(res.data);
      } catch (err) {
        toast.error("Failed to load the people waiting to be set up");
      } finally {
        setCrewLoaded(true);
      }
    })();
  }, []);

  const next = () => {
    if (!employeeId) return setError("Choose who this is for");
    if (!selectedServices.length) return setError("Choose at least one service");
    setError("");
    sessionStorage.setItem(
      "providerDetails",
      JSON.stringify({
        existing_user_id: employeeId,
        service: selectedServices.map((s) => s.id),
        specialization: selectedServices.map((s) => s.name).join(", "),
        experienceStartDate: experienceStartDate || null,
        skillKey,
      })
    );
    navigate("/providers/add/availability");
  };

  return (
    <div className="p-4" style={{ maxWidth: 560 }}>
      <div className="d-flex align-items-center gap-2 mb-4">
        <MdArrowBack style={{ cursor: "pointer" }} onClick={() => navigate("/providers")} />
        <h5 className="mb-0">Add a service provider</h5>
      </div>
      <div className="text-muted mb-4" style={{ fontSize: 13 }}>
        Pick an employee added with the Crew role. Their name, phone and other details come from their employee profile - this
        only adds what booking needs: the services they provide, their experience date, fee and shifts.
      </div>

      <Form.Group className="mb-3">
        <Form.Label>Employee</Form.Label>
        <Form.Select
          aria-label="Employee"
          value={employeeId || ""}
          onChange={(e) => {
            const picked = crew.find((c) => c.user_id === e.target.value);
            setEmployeeId(e.target.value || null);
            setEmployeeLabel(picked ? picked.full_name : "");
          }}
        >
          <option value="">Choose the employee</option>
          {crew.map((c) => (
            <option key={c.user_id} value={c.user_id}>
              {c.full_name}
              {c.employee_code ? ` · ${c.employee_code}` : ""}
            </option>
          ))}
        </Form.Select>
        {crewLoaded && crew.length === 0 ? (
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>
            Nobody is waiting. Add the person under Employees with the <strong>Crew</strong> role first, then come back here to add their services and shifts.
          </div>
        ) : null}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Services</Form.Label>
        <MultiSelect
          value={selectedServices}
          onChange={(e) => setSelectedServices(e.value)}
          options={services}
          optionLabel="name"
          filter
          placeholder="Select services"
          style={{ width: "100%", height: "40px" }}
        />
      </Form.Group>

      <SkillSelect className="mb-3" value={skillKey} onChange={setSkillKey} />

      <Form.Group className="mb-3">
        <Form.Label>Experience started on</Form.Label>
        <Form.Control type="date" value={experienceStartDate} onChange={(e) => setExperienceStartDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
      </Form.Group>

      {error ? (
        <div className="text-danger mb-3" style={{ fontSize: 13 }} role="alert">
          {error}
        </div>
      ) : null}

      <div className="d-flex gap-2">
        <SecondaryButton onClick={() => navigate("/providers")}>Cancel</SecondaryButton>
        <PrimaryButton onClick={next} disabled={!employeeId}>
          Next: services and shifts
        </PrimaryButton>
      </div>
      {employeeLabel ? (
        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          Setting up booking details for {employeeLabel}.
        </div>
      ) : null}
    </div>
  );
}
